import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Loader2, DollarSign, RefreshCw, Sparkles, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORY_LABELS = {
  finance: "💰 Kredyty/Pożyczki",
  insurance: "🛡️ Ubezpieczenia",
  banking: "🏦 Banki/Lokaty",
  legal_tax: "⚖️ Prawne/Podatki",
  real_estate: "🏠 Nieruchomości",
  health: "🏥 Zdrowie",
  auto_finance: "🚗 Leasing auto",
  energy: "☀️ Energia/Foto",
  credit_cards: "💳 Karty/BIK",
};

export default function MoneyMapPage() {
  const [stats, setStats] = useState(null);
  const [topPosts, setTopPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [prioritizing, setPrioritizing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/admin/money/stats`),
        axios.get(`${API}/admin/money/top-posts?limit=30`),
      ]);
      setStats(s.data);
      setTopPosts(p.data.posts || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rescan = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/admin/money/scan?threshold=5`);
      toast.success(`Scan OK: ${res.data.money_posts}/${res.data.scanned} money posts`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setScanning(false);
    }
  };

  const prioritize = async () => {
    setPrioritizing(true);
    try {
      const res = await axios.post(`${API}/admin/money/prioritize-queue`);
      toast.success(`Priorytety w kolejce: ${res.data.prioritized} tematów oznaczonych high`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setPrioritizing(false);
    }
  };

  if (loading || !stats) return <div className="p-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <DollarSign className="w-8 h-8 text-emerald-600" />
          <div>
            <h1 className="text-2xl font-bold">Money Map — gdzie jest kasa?</h1>
            <p className="text-sm text-muted-foreground">
              Posty z money keywords (kredyt/ubezpieczenie/prawnik/nieruchomość) — kandydaci do boost'a i affiliate.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="w-4 h-4 mr-2" /> Odśwież
          </Button>
          <Button size="sm" onClick={prioritize} disabled={prioritizing} className="bg-amber-600 hover:bg-amber-700">
            {prioritizing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Priorytetyzuj kolejkę
          </Button>
          <Button size="sm" onClick={rescan} disabled={scanning} className="bg-emerald-600 hover:bg-emerald-700">
            {scanning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DollarSign className="w-4 h-4 mr-2" />}
            Rescan
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-4">
            <p className="text-xs text-emerald-700 uppercase">Money posts</p>
            <p className="text-2xl font-bold text-emerald-900">{stats.money_posts.toLocaleString()}</p>
            <p className="text-xs text-emerald-600">{stats.money_pct}% z {stats.scanned_posts.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4">
            <p className="text-xs text-amber-700 uppercase">High-value (≥20)</p>
            <p className="text-2xl font-bold text-amber-900">{stats.high_value_posts.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50/50">
          <CardContent className="p-4">
            <p className="text-xs text-purple-700 uppercase">Very high (≥50)</p>
            <p className="text-2xl font-bold text-purple-900">{stats.very_high_posts.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-4">
            <p className="text-xs text-blue-700 uppercase">Łącznie skanowane</p>
            <p className="text-2xl font-bold text-blue-900">{stats.scanned_posts.toLocaleString()}</p>
            <p className="text-xs text-blue-600">/ {stats.total_posts.toLocaleString()} total</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">📊 Rozkład kategorii money keywords</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(stats.category_totals || {}).map(([cat, score]) => (
              <div key={cat} className="border rounded-lg p-3 bg-slate-50">
                <div className="text-xs text-slate-600">{CATEGORY_LABELS[cat] || cat}</div>
                <div className="text-xl font-bold text-slate-900">{score.toLocaleString()}</div>
                <div className="text-xs text-slate-500">total score punktów</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">💰 TOP 20 portali wg money posts</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {(stats.top_sites || []).map((s, idx) => (
              <div key={s.site_id} className="px-4 py-2 flex items-center gap-3 text-sm hover:bg-slate-50">
                <span className="text-xs text-slate-400 w-6">#{idx + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{s.name}</span>
                    {s.auto_publish ? <Badge className="text-[10px] bg-emerald-200 text-emerald-900">✓ aktywny</Badge>
                                    : <Badge variant="outline" className="text-[10px]">stand-by</Badge>}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{s.url}</div>
                </div>
                <div className="text-right text-xs">
                  <div><strong className="text-emerald-700">{s.money_post_count}</strong> money posts</div>
                  <div className="text-slate-500">avg score: {s.avg_money_score}</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">🎯 TOP 30 najbardziej zarabiających postów</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {topPosts.map((p, idx) => (
              <div key={p.id} className="px-4 py-2 flex items-center gap-3 text-sm hover:bg-slate-50">
                <span className="text-xs text-slate-400 w-6">#{idx + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{p.title}</div>
                  <div className="text-xs flex items-center gap-2 mt-1">
                    {Object.keys(p.money_categories || {}).slice(0, 3).map(cat => (
                      <Badge key={cat} className="text-[10px] bg-emerald-100 text-emerald-800">{CATEGORY_LABELS[cat]?.split(' ')[0] || cat}</Badge>
                    ))}
                  </div>
                </div>
                <div className="text-right text-xs">
                  <div>💰 <strong className="text-emerald-700">{p.money_score}</strong></div>
                  <div className="text-slate-500">Q: {p.quality_score || "—"}</div>
                </div>
                {p.published_url && (
                  <a href={p.published_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground text-center">
        💡 Money keywords: kredyt, pożyczka, ubezpieczenie, prawnik, nieruchomość, fotowoltaika, leasing — szacowany CPC 5-30 zł. Boost tych postów = realne pieniądze.
      </p>
    </div>
  );
}
